import {
  PDFCheckBox,
  PDFDocument,
  PDFDropdown,
  PDFName,
  PDFRadioGroup,
  PDFTextField,
} from 'pdf-lib';

/**
 * Ceiling on the rendered PDF font size (PDF points, ≈ pixels for our
 * purposes). Fields whose text comfortably fits their box at this size get
 * clamped down to it — stops short values like "9999" from ballooning to
 * 30 pt just because the field's authored DA said so. Longer values that
 * wouldn't fit fall back to pdf-lib's auto-fit (`setFontSize(0)`), which
 * shrinks to whatever fits inside the widget rect.
 */
const MAX_TEXT_FONT_SIZE = 12;

/**
 * Map of AcroForm field name → advisor-entered value. Strings for text /
 * radio / dropdown; booleans for checkbox-typed fields. Numbers get
 * coerced to strings at fill time (see `applyToTextField`).
 */
export type PdfFillMap = Record<string, string | boolean | number>;

/** The AcroForm field kinds we know how to fill. */
type FillableField = PDFTextField | PDFCheckBox | PDFRadioGroup | PDFDropdown;

/**
 * Load a PDF template, apply `fills` to matching AcroForm fields by name,
 * and return the saved bytes. Fields present in `fills` but absent from the
 * PDF are silently skipped (an originator may have stamped an `internalName`
 * that no longer matches the uploaded template); fields present in the PDF
 * but absent from `fills` keep their default. When several field objects share
 * one name the value fans out to all of them (see the name index below).
 *
 * The output is NOT flattened — the advisor can still edit the filled PDF
 * if they open it in Acrobat. Flattening (`form.flatten()`) is a follow-up
 * once BMO decides whether previews should be locked or editable.
 */
export async function fillPdfWithValues(
  templateBytes: ArrayBuffer,
  fills: PdfFillMap,
): Promise<Uint8Array> {
  const pdf = await PDFDocument.load(templateBytes);
  const form = pdf.getForm();

  // Index every fillable field by its (fully-qualified) name up front. A PDF
  // may hold MORE THAN ONE field object under the same name — the BMO base
  // forms do this (e.g. three "checkbox40" boxes meant to tick together).
  // pdf-lib's `form.getField(name)` returns only the FIRST match, so filling
  // through it leaves the siblings untouched; keeping every match here lets us
  // fan a single value out to all of them. Same-name fields share one value by
  // AcroForm spec, so this is exactly the intended behaviour.
  const fieldsByName = new Map<string, FillableField[]>();
  for (const f of form.getFields()) {
    if (
      f instanceof PDFTextField ||
      f instanceof PDFCheckBox ||
      f instanceof PDFRadioGroup ||
      f instanceof PDFDropdown
    ) {
      const name = f.getName();
      const bucket = fieldsByName.get(name);
      if (bucket) bucket.push(f);
      else fieldsByName.set(name, [f]);
    }
  }

  for (const [name, value] of Object.entries(fills)) {
    // Per-option AcroForm mapping supports a `fieldName:exportValue`
    // syntax for multi-widget checkbox fields (typical BMO pattern where
    // Title / Gender / Language render 5-3-2 checkboxes that all share
    // one AcroForm field name but distinct widget /AS export values like
    // "Mr." / "Mrs." / "Dr."). Splitting here lets one originator-
    // authored SelectOption.internalName tick just the right widget.
    //
    // Try the literal `name` FIRST — some AcroForm field names
    // legitimately contain colons (permitted by the PDF spec; common in
    // XFA-derived PDFs). Only fall back to the colon split when the
    // literal name doesn't exist. Keeps `Namespace:Owner`-style legit
    // names working while still supporting the export-value convention.
    const { fields, exportValue } = resolveFillTarget(fieldsByName, name);
    if (fields.length === 0) continue;

    // Apply to EVERY field carrying this name (usually one; more when the PDF
    // duplicates the field). Each type's helper is unchanged — it just runs
    // once per matching field.
    for (const field of fields) {
      if (field instanceof PDFTextField) {
        applyToTextField(field, value);
      } else if (field instanceof PDFCheckBox) {
        if (exportValue) {
          applyToMultiWidgetCheckBox(field, exportValue);
        } else {
          applyToCheckBox(field, value);
        }
      } else if (field instanceof PDFRadioGroup) {
        // For a proper radio group, the export value takes precedence over
        // the raw value — same reasoning as above.
        applyToRadioGroup(field, exportValue ?? value);
      } else if (field instanceof PDFDropdown) {
        applyToDropdown(field, value);
      }
      // Other field kinds (buttons, signatures, list boxes) are ignored —
      // none of our WorkflowField types map to them today.
    }
  }

  return pdf.save();
}

/**
 * Look up the AcroForm field(s) the caller wants to fill from the pre-built
 * name index. Tries the literal key first (some PDF field names legitimately
 * contain colons); falls back to `fieldName:exportValue` if the literal name
 * isn't registered. Returns ALL fields carrying the resolved name — a PDF can
 * hold several field objects under one name, and every one must be filled.
 * Returns `{ fields: [] }` when nothing matches — the caller skips.
 */
function resolveFillTarget(
  fieldsByName: Map<string, FillableField[]>,
  key: string,
): {
  fields: FillableField[];
  exportValue: string | null;
} {
  const literal = fieldsByName.get(key);
  if (literal) return { fields: literal, exportValue: null };
  const colonIdx = key.indexOf(':');
  if (colonIdx < 0) return { fields: [], exportValue: null };
  const split = fieldsByName.get(key.slice(0, colonIdx));
  if (!split) return { fields: [], exportValue: null };
  return { fields: split, exportValue: key.slice(colonIdx + 1) };
}

/**
 * Set a multi-widget checkbox field's value to a specific export state
 * (identified by the widget's /AS name). This is how the BMO
 * ApplicationFormv3.pdf models Title / Gender / Language — five (or three,
 * or two) checkbox widgets under one shared field name, each carrying a
 * distinct export value like "Mr.", "Mrs.", "Dr.". Calling the standard
 * `.check()` API would toggle them all together, which isn't what we
 * want; setting the acro-field value directly picks the specific widget
 * whose appearance state matches.
 *
 * We must NOT route through pdf-lib's `acroField.setValue(PDFName)` here.
 * `PDFAcroCheckBox.setValue` guards its argument against `getOnValue()` —
 * which returns only the FIRST widget's on-value — and throws
 * `InvalidAcroFieldValueError` for any other widget's export value. Since
 * these BMO "pick one of five" fields are authored as plain checkboxes
 * sharing a name (no `/Ff` Radio flag), pdf-lib models them as a checkbox,
 * so that guard silently no-ops every option except whichever happens to be
 * widget[0]. Instead we mirror what `PDFAcroRadioButton.setValue` does by
 * hand: stamp `/V` once, then flip each widget's `/AS` to its own on-value
 * (selected) or `Off`. We deliberately do NOT `markAsDirty` — the template
 * already carries per-export-value appearance streams, and leaving the field
 * clean stops pdf-lib regenerating (and clobbering) them on save.
 */
function applyToMultiWidgetCheckBox(field: PDFCheckBox, exportValue: string) {
  const on = PDFName.of(exportValue);
  const acroField = field.acroField;
  const widgets = acroField.getWidgets();
  // Only act when a widget actually carries this export state — otherwise a
  // stale originator config would stamp a bogus `/V`. Widget on-values come
  // from pdf-lib's pooled PDFName cache, so reference equality is the correct
  // check (it's exactly what pdf-lib's own setValue uses internally).
  if (!widgets.some((w) => w.getOnValue() === on)) return;
  const off = PDFName.of('Off');
  acroField.dict.set(PDFName.of('V'), on);
  for (const widget of widgets) {
    widget.setAppearanceState(widget.getOnValue() === on ? on : off);
  }
}

function applyToTextField(field: PDFTextField, value: string | boolean | number) {
  if (value === null || value === undefined) return;
  const text = String(value);
  // Smart cap: if the text would fit inside the widget's box at MAX size,
  // clamp there — otherwise drop to auto-fit (size 0) so the viewer shrinks
  // it to fit. Rough char-width estimate (0.55 × font size) is fine for the
  // common form fonts (Helvetica / Arial); a wider proportional font would
  // just fall to auto-fit sooner.
  const widgets = field.acroField.getWidgets();
  const rect = widgets[0]?.getRectangle();
  // setFontSize needs a `/DA` (default appearance) entry on the field. Some
  // PDF authors omit /DA and let the field inherit from the form's default —
  // pdf-lib throws in that case. Swallow the error: the text still fills at
  // the inherited size, we just skip our clamp for that particular field.
  try {
    if (rect) {
      const estimatedTextWidth = text.length * MAX_TEXT_FONT_SIZE * 0.55;
      if (estimatedTextWidth <= rect.width * 0.9) {
        field.setFontSize(MAX_TEXT_FONT_SIZE);
      } else {
        field.setFontSize(0);
      }
    } else {
      field.setFontSize(0);
    }
  } catch {
    /* no /DA — leave sizing at the form-level default */
  }
  field.setText(text);
}

function applyToCheckBox(field: PDFCheckBox, value: string | boolean | number) {
  // Treat "truthy-ish" as check. Advisor-side toggles carry booleans; a
  // checkbox authored against a text field (e.g. "Y"/"N") is uncommon but
  // handled — anything non-empty and non-"false" checks.
  const on =
    value === true ||
    value === 1 ||
    (typeof value === 'string' && value.length > 0 && value.toLowerCase() !== 'false');
  if (on) {
    field.check();
  } else {
    field.uncheck();
  }
}

function applyToRadioGroup(field: PDFRadioGroup, value: string | boolean | number) {
  if (value === null || value === undefined) return;
  const str = String(value);
  // `select` throws if the option name isn't a registered choice — swallow
  // so a stale originator config doesn't nuke the whole fill.
  try {
    field.select(str);
  } catch {
    /* noop */
  }
}

function applyToDropdown(field: PDFDropdown, value: string | boolean | number) {
  if (value === null || value === undefined) return;
  const str = String(value);
  try {
    field.select(str);
  } catch {
    /* noop */
  }
}
